<template>
  <aside class="innopearl_sidebar" :class="{ 'is-open': isOpen }">
    <button class="burger_btn" @click="toggleOpen" aria-label="Toggle menu">
      <SvgIcon :name="isOpen ? 'close' : 'menu'" :size="24" />
    </button>

    <div class="sidebar_logo_wrap">
      <router-link :to="logoRoute" class="sidebar_logo_link" @click="closeOnMobile">
        <img :src="require('@/assets/images/logo-simple-blanc.png')" class="sidebar_logo" alt="Innopearl" />
      </router-link>
    </div>

    <nav class="sidebar_nav">
      <div v-for="group in menuGroups" :key="group.label" class="menu_group">
        <div class="group_header">
          <span class="group_label">{{ group.label }}</span>
          <span v-if="group.locked" class="group_lock_badge" aria-label="Locked">
            <SvgIcon name="lock" :size="10" />
          </span>
        </div>

        <div class="group_items">
          <template v-for="item in group.items" :key="item.id">
            <div
              v-if="hasChildren(item) && !group.locked && !isItemLocked(item)"
              class="menu_item"
              :class="{ active: isActive(item), expanded: expanded[item.id] }"
              role="button"
              tabindex="0"
              @click="toggleExpand(item.id)"
              @keydown.enter.prevent="toggleExpand(item.id)"
              @keydown.space.prevent="toggleExpand(item.id)"
            >
              <span class="menu_item_icon"><SvgIcon :name="item.icon" :size="20" /></span>
              <span class="menu_item_label">{{ item.label }}</span>
              <SvgIcon
                name="chevronRight"
                :size="14"
                class="menu_item_chevron"
                :class="{ rotated: expanded[item.id] }"
              />
            </div>

            <router-link
              v-else-if="item.to && !group.locked && !isItemLocked(item)"
              :to="item.to"
              class="menu_item"
              :class="{ active: isActive(item) }"
            >
              <span class="menu_item_icon"><SvgIcon :name="item.icon" :size="20" /></span>
              <span class="menu_item_label">{{ item.label }}</span>
            </router-link>

            <div
              v-else
              class="menu_item locked"
              :class="{ active: isActive(item) }"
            >
              <span class="menu_item_icon"><SvgIcon :name="item.icon" :size="20" /></span>
              <span class="menu_item_label">{{ item.label }}</span>
              <span v-if="isItemLocked(item) || group.locked" class="item_lock_badge">
                <SvgIcon name="lock" :size="10" />
              </span>
            </div>

            <div
              v-if="hasChildren(item) && expanded[item.id] && !group.locked && !isItemLocked(item)"
              class="submenu"
            >
              <router-link
                v-for="child in item.children"
                :key="child.id"
                :to="child.to"
                class="submenu_item"
                :class="{ active: isActiveChild(child) }"
              >
                {{ child.label }}
              </router-link>
            </div>
          </template>
        </div>
      </div>
    </nav>

    <div class="sidebar_footer">
      <a
        href="https://outlook.office365.com/owa/calendar/innopearl@innopearl.ch/bookings/"
        target="_blank"
        class="ask_us_button"
      >
        <span class="ask_us_icon"><SvgIcon name="messageSquare" :size="20" /></span>
        <span class="ask_us_label">Ask us</span>
      </a>
    </div>
  </aside>

  <div
    v-if="isOpen"
    class="sidebar_backdrop"
    @click="isOpen = false"
  ></div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import SvgIcon from "@/components/SvgIcon.vue";

export default {
  name: "CustomSidebar",
  components: { SvgIcon },
  data() {
    return {
      isOpen: false,
      expanded: {
        growthSpaces: false,
        projects: false,
        campaigns: false,
        assessments: false,
        adminProjects: false,
        adminAssess: false,
        adminPanel: false,
      },
    };
  },
  watch: {
    $route() {
      this.isOpen = false;
    },
  },
  computed: {
    ...mapState(["role", "myProjects", "droit_access_platform"]),
    ...mapGetters({ locks: "featureLocks" }),
    logoRoute() {
      if (this.droit_access_platform === "pearlai" || this.droit_access_platform === "pearlai_and_scouting") {
        return "/where_to_innovate_finder";
      }
      if (this.role === "Expert") return "/expert/my_projects";
      if (this.role === "Admin") return "/admin/my_projects";
      return "/selectYourChallenge";
    },
    projectsRoute() {
      if (this.role === "Expert") return "/expert/my_projects";
      if (this.role === "Admin") return "/admin/my_projects";
      return "/all_my_projects";
    },
    menuGroups() {
      const isPearlaiOnly = this.droit_access_platform === "pearlai";
      const projectsList = Array.isArray(this.myProjects) ? this.myProjects : [];
      const getTimestamp = (p) => {
        const v = p?.createAt;
        if (v && v.$date && v.$date.$numberLong) return parseInt(v.$date.$numberLong, 10);
        if (typeof v === 'number') return v;
        if (p?.createAtFormatted) {
          const [d, m, y] = p.createAtFormatted.split('/').map((n) => parseInt(n, 10));
          if (d && m && y) return new Date(y, m - 1, d).getTime();
        }
        return 0;
      };
      const sortedProjects = [...projectsList].sort((a, b) => getTimestamp(b) - getTimestamp(a));
      const projectChildren = sortedProjects.slice(0, 5).map((p) => {
        const pid = (p._id && p._id.$oid) || p._id || p.id;
        return {
          id: "project_" + (pid || p.name),
          label: p.name || p.title || "Project",
          to: this.projectDetailRoute(pid),
        };
      });

      if (this.role === "Admin") {
        return [
          {
            label: "Projects",
            items: [
              {
                id: "adminProjects",
                label: "My projects",
                icon: "briefcase",
                to: "/admin/my_projects",
                children: [
                  { id: "adminProjectsAll", label: "All projects", to: "/admin/my_projects" },
                  ...projectChildren,
                ],
              },
            ],
          },
          {
            label: "Assess",
            items: [
              {
                id: "adminAssess",
                label: "Innovator assessments",
                icon: "barChart",
                children: [
                  { id: "adminManageAssess", label: "Manage assessments", to: "/admin/manage_assessments" },
                  { id: "adminCreateAssess", label: "Create an assessment", to: "/admin/internalassessment_creation" },
                  { id: "adminCriterias", label: "Manage criterias", to: "/admin/manage_assessments_criterias" },
                  { id: "adminCriteriasPublic", label: "Make criterias public", to: "/admin/manage_users_criterias" },
                  { id: "adminNextSteps", label: "Manage next steps", to: "/admin/manage_next_steps" },
                  { id: "adminDefaultMail", label: "Default mail to judges", to: "/admin/manage_default_data/6707d104621213a6cb50d58e" },
                  { id: "adminReopen", label: "Modify assessments deadline", to: "/admin/reopen_assessment" },
                ],
              },
            ],
          },
          {
            label: "Administration",
            items: [
              {
                id: "adminPanel",
                label: "Admin panel",
                icon: "database",
                children: [
                  { id: "adminManageAdmins", label: "Manage admins", to: "/admin/manage_users_admin" },
                  { id: "adminAddExpert", label: "Add an expert", to: "/register_admin" },
                  { id: "adminAssignProjects", label: "Assign projects", to: "/admin/all_projects" },
                  { id: "adminManageCustomers", label: "Manage customers", to: "/admin/manage_users" },
                  { id: "adminAddCustomer", label: "Add a customer", to: "/register" },
                  { id: "adminManagePlans", label: "Manage plans", to: "/admin/manage_plan" },
                  { id: "adminTaxonomies", label: "Manage taxonomies", to: "/admin/dict" },
                  { id: "adminInnovators", label: "Manage innovators", to: "/admin/all_startups" },
                  { id: "adminProfileFields", label: "Profile editor labels", to: "/admin/manage_profile_fields" },
                  { id: "adminPartial", label: "Profiles in progress", to: "/admin/partial_startups" },
                  { id: "adminDataroom", label: "Dataroom", to: "/pearlai_dataroom" },
                  { id: "adminDataPolicy", label: "Data policy", to: "/admin/manage_data_policy" },
                  { id: "adminTerms", label: "Terms and conditions", to: "/admin/manage_terms_and_conditions" },
                ],
              },
            ],
          },
        ];
      }

      const discoverItems = [
        { id: "discoverStart", label: "Start", icon: "compass", to: "/where_to_innovate_finder" },
        { id: "discoverChallenges", label: "My challenges", icon: "target", to: "/where_to_innovate_index" },
        {
          id: "projects",
          label: "My projects",
          icon: "briefcase",
          to: this.projectsRoute,
          locked: isPearlaiOnly,
          children: [
            { id: "pipeline", label: "Pipeline", to: this.projectsRoute },
            ...projectChildren,
          ],
        },
      ];
       const ExploreItems = [
          { id: "growthNew", label: "Start", icon: "zap", to: "/growth_engine" },
          { id: "growthChallenges", label: "Growth priorities", icon: "sprout", to: "/growth_engine_challenges" },
          { id: "growthDashboard", label: "My growth pipeline", icon: "layers", to: "/growth_engine_dashboard" },
      ];

      return [
        {
          label: "Explore",
          locked: this.locks.growth,
          items: ExploreItems,
        },
        {
          label: "Discover",
          locked: this.locks.discover,
          items: discoverItems,
        },
        {
          label: "Scout",
          locked: this.locks.scout,
          items: [
            { id: "scoutStart", label: "Start", icon: "binoculars", to: "/link_plus" },
            {
              id: "campaigns",
              label: "My campaigns",
              icon: "rocket",
              to: "/list_link_plus",
              children: [],
            },
            { id: "innovatorPool", label: "My Innovator Pool", icon: "layers", to: "/startups_ecosystem" },
          ],
        },
        {
          label: "Decide",
          locked: this.locks.assess,
          items: [
            { id: "assessStart", label: "Start", icon: "barChart", to: "/internalassessment_creation" },
            {
              id: "assessments",
              label: "My assessments",
              icon: "fileText",
              to: "/manage_assessments",
              children: [],
            },
          ],
        },
        {
          label: "Shortcuts",
          locked: this.locks.shortcuts,
          items: [
            { id: "rankings", label: "Rankings", icon: "trendingUp", to: "/rankings" },
            { id: "sharedProfiles", label: "Shared profiles", icon: "users", to: "/my_shared_lists" },
            { id: "filters", label: "Filters", icon: "pointer", to: "/list_filters" },
            { id: "innovatorFunnel", label: "My Funnel", icon: "filter", to: "/startups_stats" },
          ],
        },
      ];
    },
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    closeOnMobile() {
      if (window.innerWidth <= 1200) this.isOpen = false;
    },
    toggleExpand(id) {
      this.expanded[id] = !this.expanded[id];
    },
    hasChildren(item) {
      return Array.isArray(item.children) && item.children.length > 0;
    },
    // Verrou calculé depuis le meta de la route cible (même logique que le guard
    // router.beforeEach) : un lien dont la route exige un rôle / un droit que
    // l'utilisateur n'a pas mènerait à unauthorize(_access) -> on l'affiche verrouillé.
    routeLocked(to) {
      if (!to) return false;
      let meta = {};
      try {
        const resolved = this.$router.resolve(to);
        meta = (resolved && resolved.meta) ? resolved.meta : {};
      } catch (e) {
        return false;
      }
      if (meta.roleRestriction && this.role !== meta.roleRestriction) return true;
      const required = meta.droit_access_required;
      if (Array.isArray(required) && required.length > 0) {
        const dap = this.droit_access_platform ?? "full";
        if (!required.includes(dap)) return true;
      }
      return false;
    },
    isItemLocked(item) {
      if (item.locked) return true;
      return this.routeLocked(item.to);
    },
    projectDetailRoute(id) {
      if (!id) return this.projectsRoute;
      if (this.role === "Admin") return `/admin/project/${id}`;
      if (this.role === "Expert") return `/expert/project/${id}`;
      return `/projet/${id}`;
    },
    isActive(item) {
      if (!item.to) return false;
      if (this.$route.path === item.to) return true;
      if (item.children && item.children.some((c) => c.to === this.$route.path)) return true;
      return false;
    },
    isActiveChild(child) {
      return this.$route.path === child.to;
    },
  },
};
</script>

<style lang="css" scoped>
.innopearl_sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 240px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--gradient-blue-vertical-reverse);
  color: #FFFFFF;
  overflow: hidden;
  transition: width 0.3s ease;
  z-index: 2100;
  box-shadow: var(--shadow-sm);
}

.burger_btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: transparent;
  border: none;
  color: #FFFFFF;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}
.burger_btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.sidebar_backdrop {
  display: none;
}

.sidebar_logo_wrap {
  padding: 24px 24px 16px 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.sidebar_logo_link {
  display: block;
  width: 100%;
  text-align: center;
}
.sidebar_logo {
  height: 60px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
}

.sidebar_nav {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
  scrollbar-width: none;
}
.sidebar_nav::-webkit-scrollbar { display: none; }

.menu_group {
  margin-bottom: 8px;
}
.group_header {
  padding: 32px 16px 16px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.group_label {
  font-family: var(--font-body);
  font-size: var(--font-size-xsmall);
  font-weight: 700;
  letter-spacing: 0.6px;
  color: #FFFFFF;
}
.group_lock_badge,
.item_lock_badge {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(134, 121, 183, 0.3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #FFFFFF;
  flex-shrink: 0;
}
.item_lock_badge {
  margin-left: auto;
}

.group_items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.menu_item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: var(--radius-md);
  font-family: var(--font-body);
  font-size: var(--font-size-body);
  font-weight: 600;
  color: #FFFFFF;
  text-decoration: none;
  cursor: pointer;
  transition: background-color var(--transition-base), opacity 0.2s ease;
  position: relative;
}
.menu_item:hover:not(.locked) {
  background-color: rgba(255, 255, 255, 0.1);
}
.menu_item.active {
  font-weight: 700;
  color: #FFFFFF;
}
.menu_item.locked {
  opacity: 0.6;
  cursor: not-allowed;
}
.menu_item_icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.85);
}
.menu_item.active .menu_item_icon {
  color: #FFFFFF;
}
.menu_item_label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.menu_item_chevron {
  margin-left: auto;
  transition: transform var(--transition-base);
  color: rgba(255, 255, 255, 0.7);
  flex-shrink: 0;
}
.menu_item_chevron.rotated {
  transform: rotate(90deg);
}

.submenu {
  margin-left: 36px;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.submenu_item {
  padding: 6px 12px;
  border-radius: var(--radius-sm);
  font-family: var(--font-body);
  font-size: var(--font-size-body);
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: background-color var(--transition-base), color 0.2s ease;
}
.submenu_item:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #FFFFFF;
}
.submenu_item.active {
  color: #FFFFFF;
  font-weight: 700;
}

.sidebar_footer {
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.ask_us_button {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: var(--radius-md);
  font-family: var(--font-body);
  font-size: var(--font-size-body);
  font-weight: 600;
  color: #FFFFFF;
  text-decoration: none;
  transition: background-color var(--transition-base);
}
.ask_us_button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}
.ask_us_icon {
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

@media screen and (max-width: 1200px) {
  .innopearl_sidebar {
    width: 60px;
  }
  .innopearl_sidebar.is-open {
    width: 240px;
  }
  .burger_btn {
    display: flex;
  }
  .innopearl_sidebar:not(.is-open) .sidebar_logo_wrap,
  .innopearl_sidebar:not(.is-open) .sidebar_nav,
  .innopearl_sidebar:not(.is-open) .sidebar_footer {
    display: none;
  }
  .sidebar_backdrop {
    display: block;
    position: fixed;
    top: 0;
    left: 60px;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 2099;
  }
}
</style>
